import { Component, type ErrorInfo, type ReactNode } from 'react';
import { TrafficLightLogo } from '@/components/TrafficLightLogo';
import { RefreshCw, Home } from 'lucide-react';
import { Button } from '@/components/ui/Button';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Root-level React error boundary.
 *
 * Catches uncaught render/lifecycle errors anywhere in the component tree
 * and displays a branded fallback UI instead of a blank white screen.
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[ErrorBoundary] Uncaught render error:', error, info.componentStack);
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  private handleGoHome = (): void => {
    // Reset state then navigate — avoids re-rendering the broken tree
    this.setState({ hasError: false, error: null }, () => {
      window.location.href = '/dashboard';
    });
  };

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className="flex min-h-screen flex-col bg-[rgb(5,5,5)]">
        {/* Header — matches 404 / login */}
        <header className="flex items-center justify-between px-6 py-3 border-b border-zinc-800/60">
          <a href="/" className="flex items-center gap-2">
            <TrafficLightLogo size="sm" showSub />
          </a>
        </header>

        {/* Error content */}
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center max-w-md">
            {/* Accent icon */}
            <div className="mb-6">
              <span className="text-7xl font-bold tracking-tight bg-gradient-to-b from-red-400 via-yellow-400 to-green-400 bg-clip-text text-transparent select-none">
                ⚠
              </span>
            </div>

            <h1 className="text-2xl font-semibold text-white mb-2">
              Something went wrong
            </h1>
            <p className="text-zinc-400 mb-4">
              An unexpected error occurred. Reloading usually fixes it.
            </p>

            {/* Show error name (not the full stack) in dev-friendly way */}
            {this.state.error && (
              <p className="text-sm text-zinc-500 bg-zinc-900 border border-zinc-800 rounded px-3 py-2 mb-6 font-mono break-all">
                {this.state.error.message || 'Unknown error'}
              </p>
            )}

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button
                onClick={this.handleReload}
                className="w-full sm:w-auto bg-green-500 hover:bg-green-600 text-white"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Reload Page
              </Button>
              <Button
                onClick={this.handleGoHome}
                variant="outline"
                className="w-full sm:w-auto bg-zinc-800 border-zinc-700 hover:bg-zinc-700 text-white"
              >
                <Home className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
